import { Card, Col, Form, message, Row, Space, Typography } from 'antd';
import { VFC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SerializedInventoryMeter } from '@enmon/commons/lib/tools';
import { FormattedMessage, useIntl } from 'react-intl';
import { QrReader } from '../../../components/qr_reader/QrReader';
import { FormItem } from '../../../components/Form/FormItem';
import { Input } from '../../../components/Input/Input';
import { FORM_LAYOUT_CONFIG } from '../../../consts/common';
import { MeterIdentificationLocationState } from '../types';
import { MeterIdentificationWorkflow } from '../constants';
import { MeterIdentificationPage as messages } from '../messages';
import { useServices } from '../../../services/useServices';
import { useStore as useInventorizeStore } from '../../inventorize_meter/useStore';

interface MeterIdentificationForm {
  toolsId: string;
  apiKey: string;
}

const getNavigationRoute = (
  workflow: MeterIdentificationWorkflow | undefined,
  inventoryMeterId: SerializedInventoryMeter['id'],
) => {
  switch (workflow) {
    // TODO: installation should be renamed to sensor-installation
    case MeterIdentificationWorkflow.SensorInstallation:
      return `installation/${inventoryMeterId}`;
    case MeterIdentificationWorkflow.SensorReplacement:
      return `sensor-replacement/${inventoryMeterId}`;
    case MeterIdentificationWorkflow.MeterReplacement:
      return `meter-replacement/${inventoryMeterId}`;
    case MeterIdentificationWorkflow.ServiceInspection:
      return `service-inspection/${inventoryMeterId}`;
    default:
      return `meter/${inventoryMeterId}`;
  }
};

export const MeterIdentificationContent: VFC = () => {
  const intl = useIntl();
  const { location } = useInventorizeStore();
  const {
    api: { inventoryLocationsApiClient },
  } = useServices();
  const [form] = Form.useForm<MeterIdentificationForm>();
  const navigate = useNavigate();
  const { state } = useLocation() as { state: MeterIdentificationLocationState | undefined };
  const { workflow } = state ?? {};

  const submitForm = async (values: MeterIdentificationForm) => {
    const { apiKey, toolsId } = values;

    try {
      // Tries to verify meter via meterID first
      const meters = await inventoryLocationsApiClient.getLocationMeters({
        id: location?.id,
        filter: {
          ...(apiKey && { apiKeys: [apiKey] }),
          ...(toolsId && { toolsIds: [toolsId] }),
        },
      });

      const meter = meters?.result[0];

      if (!meter && apiKey) {
        message.error(intl.formatMessage(messages.meterErrorWithQRCode));
        return;
      }
      if (!meter && toolsId) {
        message.error(intl.formatMessage(messages.meterErrorWithStrapiId));
        return;
      }

      const meterToolsId = Number(meter?.toolsId);

      if (!meterToolsId) {
        message.error(intl.formatMessage(messages.meterError));
        return;
      }

      const navigationRoute = getNavigationRoute(workflow, meterToolsId);
      navigate(`/${navigationRoute}`);

      message.success(intl.formatMessage(messages.meterFound));
    } catch (error) {
      message.error(intl.formatMessage(messages.findError));
    }
  };

  const handleSubmit = (values: MeterIdentificationForm) => {
    void submitForm(values);
  };

  const handleScan = (result?: string | null) => {
    if (result) {
      form.setFieldsValue({ apiKey: result });
      message.success(intl.formatMessage(messages.scanQrCodeSuccess));
    }
  };

  return (
    <Card bodyStyle={{ padding: 0 }} bordered={false}>
      <Space className="w-100" direction="vertical" wrap={false}>
        <Typography.Title level={2} style={{ textAlign: 'center', fontSize: 24 }}>
          <FormattedMessage {...messages.scanOrEnterMeterId} />
        </Typography.Title>
        <Col>
          <Form
            form={form}
            id="meterIdentificationForm"
            layout="horizontal"
            name="basic"
            onFinish={handleSubmit}
            {...FORM_LAYOUT_CONFIG}
          >
            <Row justify="center">
              <Col xl={20} xs={24}>
                <QrReader onScan={handleScan} />
              </Col>
            </Row>
            <FormItem
              initialValue=""
              label="Meter Api Key"
              name="apiKey"
              tooltip={intl.formatMessage(messages.scanQrCodeTooltip)}
            >
              <Input disabled placeholder={intl.formatMessage(messages.scanQrCode)} />
            </FormItem>
            <FormItem
              initialValue=""
              label="Strapi ID"
              name="toolsId"
              tooltip={intl.formatMessage(messages.enterMeterIdTooltip)}
            >
              <Input placeholder={intl.formatMessage(messages.enterMeterId)} />
            </FormItem>
          </Form>
        </Col>
      </Space>
    </Card>
  );
};
